import { Box, Card } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import type { ReactNode } from "react";
import { brainTokens } from "../design/brainTheme";

const c = brainTokens.color;

/** Uppercase faint micro-label (DM Sans 10.5/700), used on every card header. */
export function CardLabel({ children, icon, sx }: { children: ReactNode; icon?: ReactNode; sx?: SxProps<Theme> }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.09em",
        textTransform: "uppercase",
        color: c.faint,
        ...sx,
      }}
    >
      {icon}
      <span>{children}</span>
    </Box>
  );
}

/**
 * A KPI card: uppercase faint label + big value + optional inline unit + hint.
 * Covers the 5-card header strip (valueSize 28), the 3-card overview highlights
 * (valueSize 32, unit "of 98", hint below) and the roadmap/finance count cards.
 *
 * `tileIcon` switches to the "tinted icon tile" layout (36×36 rounded tile +
 * number/label stacked beside it, detail right-aligned) used by the project
 * signal-band KPI strip — pass `tileBg`/`tileColor` for the tile's tint.
 */
export function KpiCard({
  label,
  value,
  unit,
  hint,
  valueColor = c.ink,
  valueSize = 28,
  icon,
  labelAdornment,
  tileIcon,
  tileBg,
  tileColor,
  sx,
}: {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  hint?: ReactNode;
  valueColor?: string;
  valueSize?: number;
  icon?: ReactNode;
  /** Optional node rendered on the right of the label row (e.g. an ExplainButton). */
  labelAdornment?: ReactNode;
  /** Icon name for the tinted tile layout (see `tileBg`/`tileColor`). */
  tileIcon?: ReactNode;
  tileBg?: string;
  tileColor?: string;
  sx?: SxProps<Theme>;
}) {
  if (tileIcon) {
    return (
      <Card sx={{ p: "16px 18px", display: "flex", alignItems: "center", gap: "14px", ...sx }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "9px",
            background: tileBg ?? c.tealTint,
            color: tileColor ?? c.tealInk,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: "none",
          }}
        >
          {tileIcon}
        </Box>
        <Box sx={{ flex: "none", whiteSpace: "nowrap" }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
            <Box component="span" sx={{ fontSize: valueSize, fontWeight: 700, lineHeight: 1.1, color: valueColor }}>
              {value}
            </Box>
            {unit != null && unit !== "" && (
              <Box component="span" sx={{ fontSize: 14, color: c.faint, fontWeight: 500 }}>
                {unit}
              </Box>
            )}
          </Box>
          <CardLabel sx={{ mt: "3px" }}>{label}</CardLabel>
        </Box>
        {hint != null && hint !== "" && (
          <Box
            sx={{
              fontSize: 11.5,
              color: c.muted2,
              flex: "1 1 auto",
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {hint}
          </Box>
        )}
      </Card>
    );
  }

  return (
    <Card sx={{ p: "16px 18px", ...sx }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "10px" }}>
        <CardLabel icon={icon}>{label}</CardLabel>
        {labelAdornment}
      </Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
        <Box component="span" sx={{ fontSize: valueSize, fontWeight: 700, lineHeight: 1, color: valueColor }}>
          {value}
        </Box>
        {unit != null && unit !== "" && (
          <Box component="span" sx={{ fontSize: 14, color: c.faint, fontWeight: 500 }}>
            {unit}
          </Box>
        )}
      </Box>
      {hint != null && hint !== "" && (
        <Box sx={{ fontSize: 12.5, color: c.muted2, mt: "8px" }}>{hint}</Box>
      )}
    </Card>
  );
}
